import { Box, HStack, StyleProps, Text, VStack } from '@chakra-ui/react'
import { flow } from '@helo/utils'
import type { MouseEvent, ReactNode, Ref } from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartAxisOutput } from '../ChartAxis'
import { ChartInteractionUtils, useChartInteraction } from '../ChartInteraction'
import type { ChartPieSlice, ResolvedChartPieSlice } from '../ChartPie'
import type { ChartSeriesOutput } from '../ChartSeries'
import { useChartWrapperContext } from '../ChartWrapper/ChartWrapper'
import {
  ChartTooltipConstants,
  ChartTooltipStyles,
} from './ChartTooltip.constants'
import {
  type ChartTooltipOptions,
  chartTooltipOptions,
} from './ChartTooltip.options'
import {
  ChartTooltipInputParts,
  ChartTooltipOutputParts,
  type ChartTooltipPayload,
  ChartTooltipUtils,
} from './ChartTooltip.parts'

export { chartTooltipOptions } from './ChartTooltip.options'
export type { ChartTooltipOptions } from './ChartTooltip.options'
export type {
  ChartTooltipMarkerKind,
  ChartTooltipPayload,
  ChartTooltipPayloadValue,
} from './ChartTooltip.parts'

export type ChartTooltipInput = ChartTooltipOptions

export type ChartTooltipSlice = {
  disabled: boolean
  isActive: boolean
  formatValue: (value: number) => string
  buildPayload: (
    x: string,
    series: Array<{
      seriesKey: string
      label?: string
      visualization: string
      color?: string
      fillColor?: string
      pointRadius?: number
      lineWidth?: number
      points: { x: string; y: number | null }[]
      rawPoints?: { x: string; y: number | null }[]
      formatDisplayValue?: (value: number) => string
    }>,
  ) => ChartTooltipPayload
  buildPiePayload: (
    slice: ResolvedChartPieSlice,
    pie: ChartPieSlice,
  ) => ChartTooltipPayload
}

export type ChartTooltipOutput = {
  tooltip: ChartTooltipSlice
}

const chartTooltipSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartTooltipInput,
    ChartTooltipOutput,
    Pick<QueryOutput, 'contentNode'>
  >(
    flow(
      ChartTooltipInputParts.setupInput(),
      ChartTooltipInputParts.setup_tooltipInput(),
      ChartTooltipOutputParts.setTooltip(),
    ),
  )

export interface ChartTooltipProps {
  renderConfig: ChartTooltipOutput &
    ChartSeriesOutput &
    Partial<ChartAxisOutput>
}

export interface ChartTooltipHoverGuideProps {
  renderConfig: ChartSeriesOutput
}

/** `detail` = marker + label + metrics; `label` = label-only (axis/dimension hover). */
export type ChartTooltipPanelVariant = 'detail' | 'label'

export interface ChartTooltipPanelProps extends StyleProps {
  payload: ChartTooltipPayload
  /** Display label for `payload.x`. Defaults to the domain key. */
  xLabel?: string
  variant?: ChartTooltipPanelVariant
  panelRef?: Ref<HTMLDivElement>
}

const Panel = ({
  payload,
  xLabel,
  variant = 'detail',
  panelRef,
  ...boxProps
}: ChartTooltipPanelProps): ReactNode => {
  const categoryLabel = xLabel ?? payload.x

  if (variant === 'label') {
    return (
      <Box
        ref={panelRef}
        data-testid="floating-chart-tooltip"
        {...ChartTooltipStyles.panel}
        {...boxProps}
      >
        <Text
          color="presets.neutral.0"
          fontWeight="600"
          fontSize="12px"
          noOfLines={1}
          minW={0}
        >
          {categoryLabel}
        </Text>
      </Box>
    )
  }

  const lead = payload.values[0]

  return (
    <Box
      ref={panelRef}
      data-testid="floating-chart-tooltip"
      {...ChartTooltipStyles.panel}
      {...boxProps}
    >
      <VStack alignItems="stretch" gap="8px">
        {lead != null ? (
          <HStack gap="8px" alignItems="center" minW={0}>
            <ChartTooltipUtils.SeriesMarker
              visualization={lead.visualization}
              color={lead.color}
              fillColor={lead.fillColor}
              pointRadius={lead.pointRadius}
            />
            <Text
              color="presets.neutral.0"
              fontWeight="600"
              fontSize="12px"
              noOfLines={1}
              minW={0}
            >
              {categoryLabel}
            </Text>
          </HStack>
        ) : null}
        <VStack alignItems="stretch" gap="4px">
          {payload.values.map(value => (
            <ChartTooltipUtils.TooltipKvRow
              key={value.seriesKey}
              label={value.label}
              value={value.formattedValue}
            />
          ))}
        </VStack>
      </VStack>
    </Box>
  )
}

type ClientPointer = { clientX: number; clientY: number }

/** Category hover rule for line charts. Bar charts use tooltip only. */
const HoverGuide = ({
  renderConfig,
}: ChartTooltipHoverGuideProps): ReactNode => {
  const { scales, innerWidth, innerHeight } = useChartWrapperContext()
  const { highlightedX } = useChartInteraction()
  const hasLineSeries = renderConfig.series.some(
    seriesItem => seriesItem.visualization === 'line',
  )

  if (!hasLineSeries || scales == null || highlightedX == null) return null

  const guidePos = ChartTooltipUtils.categoryGuidePosition(scales, highlightedX)
  if (guidePos == null) return null

  const isHorizontal = scales.orientation === 'horizontal'

  return isHorizontal ? (
    <line
      x1={0}
      x2={innerWidth}
      y1={guidePos}
      y2={guidePos}
      stroke={ChartTooltipConstants.hoverGuideStroke}
      strokeOpacity={ChartTooltipConstants.hoverGuideStrokeOpacity}
      strokeWidth={1}
      pointerEvents="none"
    />
  ) : (
    <line
      x1={guidePos}
      x2={guidePos}
      y1={0}
      y2={innerHeight}
      stroke={ChartTooltipConstants.hoverGuideStroke}
      strokeOpacity={ChartTooltipConstants.hoverGuideStrokeOpacity}
      strokeWidth={1}
      pointerEvents="none"
    />
  )
}

const Tooltip = ({ renderConfig }: ChartTooltipProps): ReactNode => {
  const { scales, innerWidth, innerHeight } = useChartWrapperContext()
  const { setHighlightedX, setSelectedX, onCategorySelect } =
    useChartInteraction()
  const { tooltip, series, axis } = renderConfig
  const { isActive, buildPayload } = tooltip
  const [payload, setPayload] = useState<ChartTooltipPayload | null>(null)
  const [pointer, setPointer] = useState<ClientPointer | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [adjusted, setAdjusted] = useState<{
    left: number
    top: number
  } | null>(null)

  const hoverSeries = useMemo(
    () => ChartInteractionUtils.hoverSeries(series),
    [series],
  )

  const draftLeft = pointer
    ? pointer.clientX + ChartTooltipConstants.cursorOffsetPx
    : 0
  const draftTop = pointer
    ? pointer.clientY + ChartTooltipConstants.cursorOffsetPx
    : 0

  useLayoutEffect(() => {
    if (payload == null || pointer == null) {
      setAdjusted(null)
      return
    }
    const el = panelRef.current
    if (el == null) return
    const rect = el.getBoundingClientRect()
    const next = ChartTooltipUtils.clampTooltipPosition(
      draftLeft,
      draftTop,
      rect.width,
      rect.height,
    )
    setAdjusted(prev =>
      prev?.left === next.left && prev?.top === next.top ? prev : next,
    )
  }, [payload, draftLeft, draftTop, pointer])

  if (!isActive || scales == null) return null

  const clearHover = () => {
    setPayload(null)
    setPointer(null)
    setHighlightedX(null)
  }

  const left = adjusted?.left ?? draftLeft
  const top = adjusted?.top ?? draftTop
  const isHorizontal = scales.orientation === 'horizontal'
  const categoryPosition = (value: string) =>
    ChartTooltipUtils.categoryGuidePosition(scales, value)

  const resolveNearestX = (
    event: MouseEvent<SVGRectElement>,
  ): string | null => {
    const bounds = event.currentTarget.getBoundingClientRect()
    if (bounds.width <= 0) return null
    const plotX = event.clientX - bounds.left
    const plotY = event.clientY - bounds.top
    return ChartInteractionUtils.findNearestX(
      hoverSeries,
      categoryPosition,
      isHorizontal ? plotY : plotX,
    )
  }

  const xLabel =
    payload == null
      ? undefined
      : ChartTooltipUtils.formatCategoryLabel(payload.x, {
          isDate: axis?.xAxisIsDate,
          formatState: axis?.x,
        })

  const panel =
    payload != null && pointer != null ? (
      <Panel
        panelRef={panelRef}
        payload={payload}
        xLabel={xLabel}
        position="fixed"
        left={`${left}px`}
        top={`${top}px`}
      />
    ) : null

  return (
    <g>
      <rect
        width={innerWidth}
        height={innerHeight}
        fill="transparent"
        style={{ cursor: onCategorySelect != null ? 'pointer' : undefined }}
        onMouseLeave={clearHover}
        onMouseMove={event => {
          const nearestX = resolveNearestX(event)
          if (nearestX == null) {
            clearHover()
            return
          }
          setPointer({ clientX: event.clientX, clientY: event.clientY })
          setPayload(buildPayload(nearestX, hoverSeries))
          setHighlightedX(nearestX)
        }}
        onClick={event => {
          const nearestX = resolveNearestX(event)
          if (nearestX == null) return
          setSelectedX(nearestX)
          onCategorySelect?.(nearestX)
        }}
      />
      {panel != null ? createPortal(panel, document.body) : null}
    </g>
  )
}

export const ChartTooltip = {
  options: chartTooltipOptions,
  setup: chartTooltipSetup,
  Panel,
  HoverGuide,
  Tooltip,
}
